import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowUpRight, FiX } = FiIcons;

const portfolioData = [
  {
    id: 1,
    title: "FitPair",
    featured: true,
    tags: ["Startup", "Mobile App", "Ecosystem"],
    description: "Fitness ecosystem with matchmaking, challenges, and token rewards. 40+ vendors onboarded.",
    url: "https://fit-pair.com",
    overview: "FitPair is a fitness social platform that connects users based on their workout preferences, goals, and schedules. The app features a smart matchmaking engine that pairs gym-goers with compatible fitness partners, a challenge system for friendly competition, and a token-based rewards economy that incentivises consistency and engagement. Over 40 vendors — including gyms, supplement brands, and sports retailers — have been onboarded onto the platform to redeem rewards. The product spans mobile app development, backend infrastructure, vendor partnership management, and community growth.",
  },
  {
    id: 2,
    title: "BookOTG (Book on the Go)",
    featured: true,
    tags: ["Booking", "On-Demand", "SaaS"],
    description: "On-demand booking platform starting with home barber services and expanding to lifestyle wellness.",
    url: "https://bookotg.com",
    overview: "BookOTG (Book on the Go) is an early-stage on-demand service platform designed to bring professional services directly to the customer's doorstep. Launched with a primary focus on the grooming industry, it allows users to book professional barbers to their homes with just a few clicks. The platform is architected to eventually scale into a comprehensive booking hub for various lifestyle services, including massage therapy, personal fitness instruction, and beauty services. It focuses on solving the convenience gap in traditional booking systems while providing service providers with a flexible, mobile-first business model.",
  },
  {
    id: 3,
    title: "Roaming Logos",
    featured: false,
    tags: ["AdTech", "Marketing", "Platform"],
    description: "Mobile advertising platform turning every journey into branding space for businesses.",
    url: "https://roaminglogos.com",
    overview: "Roaming Logos connects businesses with drivers for vehicle advertising campaigns. Businesses can launch campaigns, and approved drivers display branded advertisements on their vehicles while earning monthly payments. Flexible campaign durations are available for 3, 6, and 12 months.",
  },
  {
    id: 4,
    title: "BasedByte",
    featured: false,
    tags: ["Agency", "Solutions", "Marketing"],
    description: "End-to-end digital solutions company specializing in web/mobile development and growth.",
    url: "https://basedbyte.com",
    overview: "Based Byte is a Singapore-based digital solutions company that specializes in web and mobile app development, UI/UX design, branding, and full-stack digital marketing services. The company delivers end-to-end solutions from concept to deployment, helping businesses build, launch, and scale digital products with a focus on performance and growth.",
  },
  {
    id: 5,
    title: "Maison de Charm",
    featured: false,
    tags: ["Hospitality", "Luxury", "Bistro"],
    description: "French-inspired bistro offering authentic fine dining and Parisian ambience.",
    url: "https://maisondecharm.com",
    overview: "Maison de Charm is a French-inspired bistro in Singapore offering authentic fine dining experiences with classic French cuisine, premium ingredients, and a Parisian-style ambience. It focuses on delivering elegant service and a curated dining experience in a refined, romantic setting.",
  },
  {
    id: 6,
    title: "Barberhood Co",
    featured: false,
    tags: ["Booking SaaS", "Web App"],
    description: "Complete booking system and operational dashboard for modern barbershops.",
    url: "https://barberhoodco.vzy.io",
    overview: "Barberhood Co is a SaaS booking and operations platform designed specifically for modern barbershops. It provides barbers and shop owners with a complete suite of tools — online appointment booking, staff scheduling, client management, and a real-time operational dashboard.",
  },
  {
    id: 7,
    title: "Fesy Marketing",
    featured: false,
    tags: ["Corporate", "Web Design"],
    description: "Corporate website development and digital presence optimization.",
    url: "https://fesy.net",
    overview: "Fesy Marketing is a digital marketing agency with a focus on brand growth, content strategy, and performance marketing. The site was optimised for SEO, page speed, and lead generation — with a clean, conversion-focused design.",
  },
  {
    id: 8,
    title: "PrizeBlaster",
    featured: false,
    tags: ["Interactive", "Gamification"],
    description: "Interactive gamification project for enhanced user engagement.",
    url: "https://prizeblaster.com",
    overview: "PrizeBlaster is an interactive gamification platform designed to boost user engagement through prize-based mechanics. Users participate in challenges, spin-to-win games, and reward events, creating a compelling loop that drives repeat visits.",
  },
];

const OverviewModal = ({ project, onClose }) => {
  if (!project) return null;
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[200] flex items-center justify-center px-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" />
        <motion.div
          className="relative z-10 w-full max-w-2xl bg-[#0f0f0f] border border-white/10 rounded-2xl p-8 md:p-12 shadow-2xl max-h-[85vh] overflow-y-auto"
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 transition-all"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
          </button>

          <div className="flex flex-wrap gap-2 mb-6">
            {project.tags.map((tag, i) => (
              <span key={i} className="text-[10px] font-mono text-gray-400 border border-white/10 bg-black/20 px-3 py-1 rounded-full uppercase tracking-wider">
                {tag}
              </span>
            ))}
          </div>

          <h3 className="text-2xl md:text-3xl font-medium text-white mb-6 tracking-tight">
            {project.title}
          </h3>

          <div className="text-gray-400 font-light leading-relaxed text-sm md:text-base mb-10 whitespace-pre-line">
            {project.overview}
          </div>

          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-white border border-white/20 px-6 py-3 rounded-full hover:bg-white hover:text-black transition-all duration-300"
          >
            Visit Website
            <SafeIcon icon={FiArrowUpRight} className="w-4 h-4" />
          </a>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const PortfolioSection = () => {
  const sectionRef = useRef(null);
  const [activeProject, setActiveProject] = useState(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(sectionRef.current, {
        filter: "blur(8px)",
        opacity: 0.6,
        scale: 0.98,
        ease: "none",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "bottom 40%",
          end: "bottom top",
          scrub: true,
        },
      });

      gsap.from(".portfolio-card", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 70%",
          end: "bottom 90%",
          toggleActions: "play none none reverse",
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <section
        ref={sectionRef}
        id="portfolio"
        className="relative min-h-screen w-full bg-[#050505] py-24 px-6 md:px-12 overflow-hidden will-change-transform"
      >
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="mb-16 md:mb-24">
            <h4 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-4">
              Portfolio
            </h4>
            <h2 className="text-4xl md:text-6xl font-light text-white mb-6 tracking-tight">
              Selected Work
            </h2>
            <p className="text-gray-400 text-lg md:text-xl font-light max-w-2xl">
              Platforms, systems, and businesses I've built, launched, and scaled.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {portfolioData.map((project) => (
              <div
                key={project.id}
                className="portfolio-card group relative p-8 md:p-10 border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors duration-500 flex flex-col justify-between h-full"
              >
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag, i) => (
                        <span key={i} className="text-[10px] md:text-xs font-mono text-gray-400 border border-white/10 bg-black/20 px-3 py-1 rounded-full uppercase tracking-wider">
                          {tag}
                        </span>
                      ))}
                    </div>
                    {project.featured && (
                      <span className="text-[10px] md:text-xs font-bold text-black bg-white px-3 py-1 rounded-full uppercase tracking-wider">
                        Featured
                      </span>
                    )}
                  </div>

                  <h3 className="text-2xl md:text-3xl font-medium text-white mb-4 tracking-tight group-hover:text-gray-200 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 font-light leading-relaxed mb-12 text-sm md:text-base">
                    {project.description}
                  </p>
                </div>

                <div className="flex items-center gap-6 mt-auto pt-6 border-t border-white/10">
                  <button
                    onClick={() => setActiveProject(project)}
                    className="text-sm font-medium text-white hover:text-gray-200 transition-colors tracking-wide border-b border-transparent hover:border-white/20 pb-0.5"
                  >
                    Learn More
                  </button>
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-gray-500 hover:text-white transition-colors flex items-center gap-1 tracking-wide group/link"
                  >
                    Visit Website
                    <SafeIcon icon={FiArrowUpRight} className="w-4 h-4 transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {activeProject && (
        <OverviewModal project={activeProject} onClose={() => setActiveProject(null)} />
      )}
    </>
  );
};

export default PortfolioSection;